import React, {createContext, useContext, useEffect, useState, useRef, useCallback} from 'react';
import {getAuth, GoogleAuthProvider, sendPasswordResetEmail, signInWithPopup, onAuthStateChanged, setPersistence, browserLocalPersistence, onIdTokenChanged, signOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification} from "firebase/auth";
import {FetchContext} from "./FetchContext";
import {LibrettoPlan} from "../utils/utils";
import mixpanel from "mixpanel-browser";
import {doc, getDoc, onSnapshot} from "firebase/firestore";
import {db} from "../Firebase";
import {kLibrettoUserCollectionName} from "../utils/CollectionConstants";

const AuthContext = createContext();
const { Provider } = AuthContext;

// Storage keys
const kIsAuthenticated = 'isAuthenticatedOnLibretto';
const kSubscriptionStatus = 'librettoSubscriptionStatus';
const kLibrettoToken = 'librettoUserToken';
const kLibrettoUserId = 'librettoUserId';
const kLibrettoDisplayName = 'librettoDisplayName';
const kLibrettoEmail = 'librettoEmail';
const kLibrettoUserCreatedAt = 'librettoUserCreatedAt';
const kLibrettoPhotoUrl = 'librettoPhotoUrl';
const kLibrettoLiveblocksToken = 'librettoLiveblocksToken';
const kLibrettoCurrentProjectId = 'librettoCurrentProjectId';
const kLibrettoUserPlan = 'librettoUserPlan';
const kLibrettoUserRoomName = 'librettoUserRoomName';
const kLibrettoDefaultWorkspaceId = 'librettoDefaultWorkspaceId';
const kLibrettoWorkspaces = 'librettoWorkspaces';
const kLibrettoCurrentWorkspaceId = 'librettoCurrentWorkspaceId';
const kUserEmailVerified = 'librettoUserEmailVerified';
const kBackendAuthenticated = 'librettoBackendAuthenticated';
const kUpgradeIntent = 'librettoUpgradeIntent';
const kIsTestUser = 'librettoIsTestUser';
const kHasLegacyProjects = 'librettoHasLegacyProjects';
const kHasExportedAComposition = 'librettoHasExportedAComposition';
const kSignupAttributionFirstTouch = 'librettoSignupAttributionFirstTouch';
const kDefaultSignupPlan = LibrettoPlan.StudioFree;
const kDesktopLocalUserId = 'local-desktop-user';
const kDesktopLocalWorkspaceId = 'local-workspace';

const isDesktopLocalModeEnabled = () => {
    try {
        return typeof window !== 'undefined' && window.__LIBRETTO_DESKTOP_LOCAL__ === true;
    } catch (_e) {
        return false;
    }
};

const kAttributionQueryParamToField = [
    ['utm_source', 'utmSource'],
    ['utm_campaign', 'utmCampaign'],
    ['utm_medium', 'utmMedium'],
    ['utm_term', 'utmTerm'],
    ['utm_content', 'utmContent'],
    ['gclid', 'gclid'],
    ['fbclid', 'fbclid'],
];

const kValidLibrettoPlans = new Set(Object.values(LibrettoPlan));

const normalizeSignupPlanSelection = (value) => {
    const rawPlan = value && typeof value === 'object'
        ? value.librettoPlan ?? value.LibrettoPlan ?? value.plan
        : value;
    const parsedPlan = typeof rawPlan === 'string' && rawPlan.trim() !== ''
        ? Number(rawPlan)
        : rawPlan;
    return kValidLibrettoPlans.has(parsedPlan) ? parsedPlan : kDefaultSignupPlan;
};

// Prefer localStorage for auth/session continuity across tabs; fall back to sessionStorage/memory when unavailable.
const createMemoryStorage = () => {
    const map = new Map();
    return {
        getItem: (key) => (map.has(key) ? map.get(key) : null),
        setItem: (key, value) => map.set(key, String(value)),
        removeItem: (key) => map.delete(key),
    };
};

const memoryStorage = createMemoryStorage();

const getBestAvailableStorage = () => {
    if (typeof window === 'undefined') return memoryStorage;

    const tryStorage = (candidate) => {
        try {
            const testKey = '__libretto_storage_test__';
            candidate.setItem(testKey, '1');
            candidate.removeItem(testKey);
            return candidate;
        } catch (_e) {
            return null;
        }
    };

    return tryStorage(window.localStorage) || tryStorage(window.sessionStorage) || memoryStorage;
};

const authStorage = getBestAvailableStorage();
const tabStorage = (typeof window !== 'undefined' && (() => {
    try {
        return window.sessionStorage;
    } catch (_e) {
        return authStorage;
    }
})()) || authStorage;

const safeJsonParse = (value, fallback) => {
    if (value == null || value === '') return fallback;
    try {
        return JSON.parse(value);
    } catch (_e) {
        return fallback;
    }
};

const trimString = (value) => {
    if (typeof value !== 'string') return '';
    return value.trim();
};

const sanitizeSignupAttribution = (value) => {
    if (!value || typeof value !== 'object') return null;

    const out = {};
    for (const [, field] of kAttributionQueryParamToField) {
        const nextValue = trimString(value[field]);
        if (nextValue) out[field] = nextValue;
    }

    const landingPath = trimString(value.landingPath);
    if (landingPath) out.landingPath = landingPath;

    const capturedAt = Number(value.capturedAt);
    if (Number.isFinite(capturedAt) && capturedAt > 0) {
        out.capturedAt = Math.floor(capturedAt);
    }

    const hasAttributionValues = kAttributionQueryParamToField.some(([, field]) => Boolean(out[field]));
    if (!hasAttributionValues) return null;

    if (!out.capturedAt) {
        out.capturedAt = Math.floor(Date.now() / 1000);
    }

    return out;
};

const parseSignupAttributionFromLocation = (locationLike) => {
    const search = typeof locationLike?.search === 'string' ? locationLike.search : '';
    if (!search) return null;

    let params;
    try {
        params = new URLSearchParams(search);
    } catch (_e) {
        return null;
    }

    const candidate = {};
    for (const [queryParam, field] of kAttributionQueryParamToField) {
        const value = trimString(params.get(queryParam));
        if (value) {
            candidate[field] = value;
        }
    }

    candidate.landingPath = trimString(locationLike?.pathname) || '/';
    candidate.capturedAt = Math.floor(Date.now() / 1000);

    return sanitizeSignupAttribution(candidate);
};

const getStoredSignupAttribution = () => {
    const parsed = safeJsonParse(authStorage.getItem(kSignupAttributionFirstTouch), null);
    return sanitizeSignupAttribution(parsed);
};

const maybeCaptureSignupAttributionFirstTouch = () => {
    if (typeof window === 'undefined') return null;

    const existing = getStoredSignupAttribution();
    if (existing) return existing;

    const parsed = parseSignupAttributionFromLocation(window.location);
    if (!parsed) return null;

    try {
        authStorage.setItem(kSignupAttributionFirstTouch, JSON.stringify(parsed));
    } catch (_e) {
        // Ignore storage errors; signup payload will simply omit attribution.
    }
    return parsed;
};

const normalizeWorkspaceList = (workspaces) => {
    if (!Array.isArray(workspaces)) return [];
    return workspaces
        .map((w) => ({
            workspaceId: String(w?.workspaceId ?? ''),
            name: typeof w?.name === 'string' ? w.name : '',
            role: typeof w?.role === 'string' ? w.role : '',
            ownerUserId: typeof w?.ownerUserId === 'string' ? w.ownerUserId : '',
        }))
        .filter((w) => w.workspaceId.length > 0);
};

const pickInitialWorkspaceId = ({ persistedWorkspaceId, defaultWorkspaceId, workspaces, fallbackUserId }) => {
    const list = normalizeWorkspaceList(workspaces);
    const has = (id) => id && list.some((w) => w.workspaceId === id);
    if (has(persistedWorkspaceId)) return persistedWorkspaceId;
    if (has(defaultWorkspaceId)) return defaultWorkspaceId;
    if (list.length > 0) return list[0].workspaceId;
    return defaultWorkspaceId || fallbackUserId || null;
};

const AuthProvider = ({ children }) => {
    const firebaseAuth = getAuth();
    const fetchContext = useContext(FetchContext);
    const isDesktopLocalMode = isDesktopLocalModeEnabled();

    const [authReady, setAuthReady] = useState(false);
    const authReadyRef = useRef(false);

    const markAuthReady = useCallback(() => {
        if (authReadyRef.current) return;
        authReadyRef.current = true;
        setAuthReady(true);
    }, []);

    // Initialize state from storage
    const [authState, setAuthState] = useState({
        isAuthenticated: authStorage.getItem(kIsAuthenticated),
        emailVerified: authStorage.getItem(kUserEmailVerified),
        subscriptionStatus: authStorage.getItem(kSubscriptionStatus),
        token: authStorage.getItem(kLibrettoToken),
        userId: authStorage.getItem(kLibrettoUserId),
        displayName: authStorage.getItem(kLibrettoDisplayName),
        roomName: authStorage.getItem(kLibrettoUserRoomName),
        email: authStorage.getItem(kLibrettoEmail),
        userPlan: authStorage.getItem(kLibrettoUserPlan),
        photoUrl: authStorage.getItem(kLibrettoPhotoUrl),
        isTestUser: authStorage.getItem(kIsTestUser) === 'true',
        hasLegacyProjects: authStorage.getItem(kHasLegacyProjects) === 'true',
        hasExportedAComposition: authStorage.getItem(kHasExportedAComposition) === 'true',
        defaultWorkspaceId: authStorage.getItem(kLibrettoDefaultWorkspaceId),
        currentWorkspaceId: authStorage.getItem(kLibrettoCurrentWorkspaceId),
        workspaces: normalizeWorkspaceList(safeJsonParse(authStorage.getItem(kLibrettoWorkspaces), [])),
    });

    // Track if we've completed initial backend authentication
    const backendAuthenticatedRef = useRef(authStorage.getItem(kBackendAuthenticated) === 'true');
    
    // Track if auth initialization is in progress to avoid duplicate calls
    const authInitInProgressRef = useRef(false);

    // When true, onAuthStateChanged should not auto-call backend /login because an explicit flow is doing it.
    const suppressAutoBackendLoginRef = useRef(false);

    // Backend /login retry state (for transient outages/network issues).
    const backendLoginRetryTimeoutRef = useRef(null);
    const backendLoginRetryAttemptRef = useRef(0);

    // Track if a logout was explicitly requested (vs spurious Firebase Auth state change)
    const logoutIntentRef = useRef(false);
    
    // Track the last known user UID to detect spurious sign-outs
    const lastKnownUserRef = useRef(authStorage.getItem(kLibrettoUserId));

    // Login/Signup form state
    const [loginFormOpen, setLoginFormOpen] = useState(false);
    const [signupFormOpen, setSignupFormOpen] = useState(false);
    const [selectedLibrettoPlanForSignup, setSelectedLibrettoPlanForSignup] = useState(kDefaultSignupPlan);
    const [signUpStateMessage, setSignUpStateMessage] = useState({forSignup: false, forSignIn: false, message: "", user: null});
    const [signUpStateMessageOpen, setSignUpStateMessageOpen] = useState(false);

    const provider = new GoogleAuthProvider();

    useEffect(() => {
        if (!isDesktopLocalMode) return;

        const localWorkspaces = [{
            workspaceId: kDesktopLocalWorkspaceId,
            name: 'Local Workspace',
            role: 'owner',
            ownerUserId: kDesktopLocalUserId,
        }];

        authStorage.setItem(kIsAuthenticated, 'true');
        authStorage.setItem(kUserEmailVerified, 'true');
        authStorage.setItem(kBackendAuthenticated, 'true');
        authStorage.setItem(kSubscriptionStatus, 'active');
        authStorage.setItem(kLibrettoUserPlan, String(LibrettoPlan.StudioFree));
        authStorage.setItem(kLibrettoToken, 'local-desktop-token');
        authStorage.setItem(kLibrettoUserId, kDesktopLocalUserId);
        authStorage.setItem(kLibrettoDisplayName, 'Local User');
        authStorage.setItem(kLibrettoEmail, 'local@libretto.app');
        authStorage.setItem(kLibrettoDefaultWorkspaceId, kDesktopLocalWorkspaceId);
        authStorage.setItem(kLibrettoCurrentWorkspaceId, kDesktopLocalWorkspaceId);
        authStorage.setItem(kLibrettoWorkspaces, JSON.stringify(localWorkspaces));

        backendAuthenticatedRef.current = true;
        setAuthState((prevState) => ({
            ...prevState,
            isAuthenticated: 'true',
            emailVerified: 'true',
            subscriptionStatus: 'active',
            token: 'local-desktop-token',
            userId: kDesktopLocalUserId,
            displayName: 'Local User',
            email: 'local@libretto.app',
            userPlan: LibrettoPlan.StudioFree,
            isTestUser: false,
            defaultWorkspaceId: kDesktopLocalWorkspaceId,
            currentWorkspaceId: kDesktopLocalWorkspaceId,
            workspaces: localWorkspaces,
        }));
        markAuthReady();
    }, [isDesktopLocalMode, markAuthReady]);

    useEffect(() => {
        maybeCaptureSignupAttributionFirstTouch();
    }, []);

    // ==================== Helper Functions ====================

    const setAuthInfo = useCallback(({ isAuthenticated, emailVerified, subscriptionStatus, token, userId, displayName, email, photoUrl, userPlan, roomName, createdAt, isTestUser, hasLegacyProjects, hasExportedAComposition }) => {
        authStorage.setItem(kIsAuthenticated, isAuthenticated === true ? 'true' : String(isAuthenticated));
        authStorage.setItem(kSubscriptionStatus, subscriptionStatus ?? "");
        authStorage.setItem(kUserEmailVerified, emailVerified === true ? 'true' : String(emailVerified));
        if (token != null) authStorage.setItem(kLibrettoToken, token);
        if (userId != null) authStorage.setItem(kLibrettoUserId, userId);
        authStorage.setItem(kLibrettoDisplayName, displayName ?? "");
        authStorage.setItem(kLibrettoEmail, email ?? "");
        authStorage.setItem(kLibrettoUserCreatedAt, createdAt ?? "");
        authStorage.setItem(kLibrettoPhotoUrl, photoUrl ?? "");
        authStorage.setItem(kLibrettoUserPlan, userPlan ?? "");
        authStorage.setItem(kLibrettoUserRoomName, roomName ?? "");
        authStorage.setItem(kIsTestUser, isTestUser === true ? 'true' : 'false');
        authStorage.setItem(kHasLegacyProjects, hasLegacyProjects === true ? 'true' : 'false');
        authStorage.setItem(kHasExportedAComposition, hasExportedAComposition === true ? 'true' : 'false');
        setAuthState({
            isAuthenticated,
            subscriptionStatus,
            emailVerified,
            token,
            userId,
            displayName,
            roomName,
            email,
            photoUrl,
            userPlan,
            isTestUser: isTestUser === true,
            hasLegacyProjects: hasLegacyProjects === true,
            hasExportedAComposition: hasExportedAComposition === true,
            defaultWorkspaceId: authStorage.getItem(kLibrettoDefaultWorkspaceId),
            currentWorkspaceId: authStorage.getItem(kLibrettoCurrentWorkspaceId),
            workspaces: normalizeWorkspaceList(safeJsonParse(authStorage.getItem(kLibrettoWorkspaces), [])),
        });
    }, []);

    const clearAuthInfo = useCallback(() => {
        authStorage.removeItem(kIsAuthenticated);
        authStorage.removeItem(kSubscriptionStatus);
        authStorage.removeItem(kUserEmailVerified);
        authStorage.removeItem(kLibrettoToken);
        authStorage.removeItem(kLibrettoUserId);
        authStorage.removeItem(kLibrettoDisplayName);
        authStorage.removeItem(kLibrettoEmail);
        authStorage.removeItem(kLibrettoUserCreatedAt);
        authStorage.removeItem(kLibrettoPhotoUrl);
        authStorage.removeItem(kLibrettoUserPlan);
        authStorage.removeItem(kLibrettoUserRoomName);
        authStorage.removeItem(kLibrettoDefaultWorkspaceId);
        authStorage.removeItem(kLibrettoWorkspaces);
        authStorage.removeItem(kLibrettoCurrentWorkspaceId);
        authStorage.removeItem(kBackendAuthenticated);
        authStorage.removeItem(kIsTestUser);
        authStorage.removeItem(kHasLegacyProjects);
        authStorage.removeItem(kHasExportedAComposition);
        backendAuthenticatedRef.current = false;
        setAuthState({
            isAuthenticated: 'false',
            subscriptionStatus: "",
            emailVerified: 'false',
            token: null,
            userId: null,
            displayName: null,
            roomName: null,
            email: null,
            photoUrl: null,
            userPlan: null,
            isTestUser: false,
            hasLegacyProjects: false,
            hasExportedAComposition: false,
            defaultWorkspaceId: null,
            currentWorkspaceId: null,
            workspaces: [],
        });
    }, []);

    const showAuthPopupBlockedMessage = useCallback((mode = "signin") => {
        const forSignup = mode === "signup";
        const forSignIn = !forSignup;
        setSignUpStateMessage({
            forSignup,
            forSignIn,
            message: "Popup blocked. Please allow popups for this site and try again.",
            user: null,
        });
        setSignUpStateMessageOpen(true);
    }, []);

    // Ensure persistence is configured once to avoid async gaps before popup auth.
    useEffect(() => {
        if (isDesktopLocalMode) return;
        setPersistence(firebaseAuth, browserLocalPersistence).catch((error) => {
            console.warn("Failed to set auth persistence:", error);
        });
    }, [firebaseAuth, isDesktopLocalMode]);

    const getSubscriptionStatusFromStorage = useCallback(async ({userId}) => {
        try {
            const entityRef = doc(db, kLibrettoUserCollectionName, userId);
            const docSnap = await getDoc(entityRef);
            if (!docSnap?.exists() || !docSnap.data()) {
                return {status: "active", userPlan: LibrettoPlan.StudioFree, roomName: null};
            }
            const data = docSnap.data();
            const status = data.subscriptionStatus;
            const userPlan = data.plan;
            const roomName = data.roomName;
            authStorage.setItem(kSubscriptionStatus, status ?? "");
            authStorage.setItem(kLibrettoUserPlan, userPlan ?? "");
            authStorage.setItem(kLibrettoUserRoomName, roomName ?? "");
            setAuthState(prevState => ({...prevState, subscriptionStatus: status, userPlan, roomName}));
            return {status, userPlan, roomName};
        } catch (error) {
            console.error("Error fetching subscription status:", error);
            // Don't fail auth on Firestore errors - return defaults
            return {status: "active", userPlan: LibrettoPlan.StudioFree, roomName: null};
        }
    }, []);

    // ==================== Token Management ====================

    /**
     * Get a fresh token for API calls. This method:
     * 1. Always returns a valid, non-expired token
     * 2. Automatically refreshes if needed
     * 3. Updates stored token for API calls
     * 
     * Use this for all custom backend API calls.
     * Note: Firestore handles its own token refresh automatically - you don't need this for Firestore.
     */
    const getFreshToken = useCallback(async () => {
        if (isDesktopLocalMode) {
            return authStorage.getItem(kLibrettoToken) || 'local-desktop-token';
        }
        const user = firebaseAuth.currentUser;
        if (!user) {
            return null;
        }
        try {
            // getIdToken() automatically refreshes if the token is expired or close to expiring
            const idToken = await user.getIdToken();
            authStorage.setItem(kLibrettoToken, idToken);
            setAuthState(prevState => ({...prevState, token: idToken}));
            return idToken;
        } catch (error) {
            console.error("Error getting fresh token:", error);
            // Don't sign out on token errors - let Firebase handle recovery
            return authStorage.getItem(kLibrettoToken);
        }
    }, [firebaseAuth, isDesktopLocalMode]);

    /**
     * Force refresh the token. Use sparingly - normally getFreshToken() is sufficient.
     */
    const inFlightTokenRefreshRef = useRef(null);
    const lastForcedTokenRefreshAtRef = useRef(0);

    const refreshToken = useCallback(async () => {
        if (isDesktopLocalMode) {
            return authStorage.getItem(kLibrettoToken) || 'local-desktop-token';
        }
        const user = firebaseAuth.currentUser;
        if (!user) {
            return null;
        }
        if (inFlightTokenRefreshRef.current) {
            return inFlightTokenRefreshRef.current;
        }

        const now = Date.now();
        // Avoid hammering Firebase Auth with forced refreshes (can trigger auth/quota-exceeded).
        // If we've forced a refresh very recently, do a non-forced getIdToken() instead.
        const shouldForce = now - lastForcedTokenRefreshAtRef.current > 30_000;

        const refreshPromise = (async () => {
            try {
                const idToken = await user.getIdToken(shouldForce);
                if (shouldForce) {
                    lastForcedTokenRefreshAtRef.current = now;
                }
                authStorage.setItem(kLibrettoToken, idToken);
                setAuthState(prevState => ({...prevState, token: idToken}));
                return idToken;
            } catch (error) {
                console.error("Error refreshing token:", error);
                return null;
            } finally {
                inFlightTokenRefreshRef.current = null;
            }
        })();

        inFlightTokenRefreshRef.current = refreshPromise;
        return refreshPromise;
    }, [firebaseAuth, isDesktopLocalMode]);

    // Legacy getToken for backward compatibility - returns cached token
    const getToken = useCallback(() => {
        return authStorage.getItem(kLibrettoToken);
    }, []);

    // Automatically recover from token-expiration 401s by refreshing the Firebase ID token once and retrying.
    // This prevents lots of callsites from force-logging-out on transient 401s.
    useEffect(() => {
        if (isDesktopLocalMode) return;
        const axiosInstance = fetchContext?.authAxios;
        if (!axiosInstance) return;

        const responseInterceptorId = axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const status = error?.response?.status;
                const originalRequest = error?.config;
                if (status !== 401 || !originalRequest) {
                    return Promise.reject(error);
                }

                if (originalRequest.__librettoAuthRetry) {
                    return Promise.reject(error);
                }
                originalRequest.__librettoAuthRetry = true;

                const currentUser = firebaseAuth.currentUser;
                if (!currentUser) {
                    return Promise.reject(error);
                }

                const headers = originalRequest.headers ?? {};
                const getHeader = (name) => {
                    if (headers && typeof headers.get === 'function') {
                        return headers.get(name);
                    }
                    return headers?.[name] ?? headers?.[name.toLowerCase()];
                };

                const currentAuthHeader = getHeader('Authorization');
                const storedToken = authStorage.getItem(kLibrettoToken);
                const storedAuthHeader = storedToken ? `Bearer ${storedToken}` : null;

                // Only retry for requests that are using the current Libretto Firebase token (avoid guest/livekit tokens).
                const shouldRetry =
                    !currentAuthHeader ||
                    currentAuthHeader === storedAuthHeader ||
                    (!storedToken && (currentAuthHeader === 'Bearer null' || currentAuthHeader === 'Bearer undefined'));

                if (!shouldRetry) {
                    return Promise.reject(error);
                }

                const newToken = await refreshToken();
                if (!newToken) {
                    return Promise.reject(error);
                }

                const newAuthHeader = `Bearer ${newToken}`;
                if (headers && typeof headers.set === 'function') {
                    headers.set('Authorization', newAuthHeader);
                } else {
                    headers.Authorization = newAuthHeader;
                }
                originalRequest.headers = headers;

                return axiosInstance(originalRequest);
            }
        );

        return () => {
            axiosInstance.interceptors.response.eject(responseInterceptorId);
        };
    }, [fetchContext?.authAxios, firebaseAuth, refreshToken, isDesktopLocalMode]);

    // ==================== Backend Authentication Helpers ====================

    const clearBackendLoginRetry = useCallback(() => {
        if (backendLoginRetryTimeoutRef.current) {
            clearTimeout(backendLoginRetryTimeoutRef.current);
            backendLoginRetryTimeoutRef.current = null;
        }
        backendLoginRetryAttemptRef.current = 0;
    }, []);

    const applyBackendLoginSuccess = useCallback(({user, token, email, photoUrl, backendData, trackMixpanelEvent}) => {
        clearBackendLoginRetry();

        authStorage.setItem(kBackendAuthenticated, "true");
        backendAuthenticatedRef.current = true;

        const subscriptionStatus = backendData?.subscriptionStatus || 'active';
        const userPlan = backendData?.userPlan || LibrettoPlan.StudioFree;
        const roomName = backendData?.roomName || null;
        const displayName = backendData?.displayName ?? user?.displayName;
        const isTestUser = backendData?.isTestUser === true;
        const hasLegacyProjects = backendData?.hasLegacyProjects === true;
        const hasExportedAComposition = backendData?.hasExportedAComposition === true;

        const defaultWorkspaceId = String(backendData?.defaultWorkspaceId || user?.uid || '');
        const workspaces = normalizeWorkspaceList(backendData?.workspaces);
        const persistedWorkspaceId = authStorage.getItem(kLibrettoCurrentWorkspaceId);
        const currentWorkspaceId = pickInitialWorkspaceId({
            persistedWorkspaceId,
            defaultWorkspaceId,
            workspaces,
            fallbackUserId: user?.uid,
        });

        if (defaultWorkspaceId) authStorage.setItem(kLibrettoDefaultWorkspaceId, defaultWorkspaceId);
        authStorage.setItem(kLibrettoWorkspaces, JSON.stringify(workspaces));
        if (currentWorkspaceId) authStorage.setItem(kLibrettoCurrentWorkspaceId, currentWorkspaceId);

        if (trackMixpanelEvent) {
            mixpanel.track('Sign In', {
                'email': email ?? user?.email,
                'userId': user?.uid,
                'plan': userPlan,
            });
        }

        setAuthInfo({
            isAuthenticated: true,
            emailVerified: "true",
            subscriptionStatus,
            token,
            userId: user?.uid,
            email: email ?? user?.email,
            displayName,
            roomName,
            photoUrl: photoUrl ?? user?.photoURL,
            userPlan,
            createdAt: user?.metadata?.creationTime,
            isTestUser,
            hasLegacyProjects,
            hasExportedAComposition,
        });
        markAuthReady();
    }, [clearBackendLoginRetry, setAuthInfo, markAuthReady]);

    const attemptBackendLogin = useCallback(async ({
        user,
        token,
        email,
        photoUrl,
        trackMixpanelEvent = false,
        showUserMessages = false,
    }) => {
        if (!user) return {ok: false, shouldRetry: false, error: new Error('No user')};

        const resolvedToken = token ?? await user.getIdToken();
        authStorage.setItem(kLibrettoToken, resolvedToken);

        try {
            const response = await fetchContext.authAxios.get('/login', {
                headers: { Authorization: `Bearer ${resolvedToken}` }
            });

            if (response.status === 200) {
                applyBackendLoginSuccess({
                    user,
                    token: resolvedToken,
                    email,
                    photoUrl,
                    backendData: response.data,
                    trackMixpanelEvent,
                });
                return {ok: true, shouldRetry: false, error: null};
            }

            return {ok: false, shouldRetry: true, error: new Error(`Unexpected status: ${response.status}`)};
        } catch (error) {
            const status = error.response?.status;
            const shouldRetry = status == null || status >= 500;

            if (showUserMessages) {
                if (status === 401) {
                    setSignUpStateMessage({forSignup: false, forSignIn: true, message: "Invalid credentials. Please make sure you have created an account and entered valid credentials!", user: null});
                    setSignUpStateMessageOpen(true);
                } else if (status === 403) {
                    setSignUpStateMessage({forSignup: false, forSignIn: true, message: "The email address is not verified. Please verify your email by clicking the link we sent!", user});
                    setSignUpStateMessageOpen(true);
                } else if (status === 451) {
                    setSignUpStateMessage({forSignup: false, forSignIn: true, message: "Unable to login. Please email contact@libretto.fm if this persists.", user: null});
                    setSignUpStateMessageOpen(true);
                } else if (status === 406) {
                    setSignUpStateMessage({forSignup: false, forSignIn: true, message: "Invite-only access. Please email contact@libretto.fm to get invited or if you should already have access.", user: null});
                    setSignUpStateMessageOpen(true);
                } else if (status === 500) {
                    setSignUpStateMessage({forSignup: false, forSignIn: true, message: "Internal server error. Please try again later!", user: null});
                    setSignUpStateMessageOpen(true);
                } else if (status != null) {
                    console.error("Login error:", error);
                } else {
                    // Network error or other - don't show user error, let them retry.
                    console.error("Login error:", error);
                }
            } else {
                console.error("Backend login error:", error);
            }

            return {ok: false, shouldRetry, error};
        }
    }, [fetchContext.authAxios, applyBackendLoginSuccess]);

    const scheduleBackendLoginRetry = useCallback(() => {
        if (backendLoginRetryTimeoutRef.current) return;

        const attempt = backendLoginRetryAttemptRef.current;
        const delayMs = Math.min(30_000, 1000 * (2 ** attempt));
        backendLoginRetryAttemptRef.current = attempt + 1;

        backendLoginRetryTimeoutRef.current = setTimeout(async () => {
            backendLoginRetryTimeoutRef.current = null;

            const user = firebaseAuth.currentUser;
            if (!user) {
                clearBackendLoginRetry();
                return;
            }

            if (backendAuthenticatedRef.current) {
                clearBackendLoginRetry();
                return;
            }
            if (suppressAutoBackendLoginRef.current) {
                // An explicit auth flow is in progress; try again later.
                scheduleBackendLoginRetry();
                return;
            }

            const result = await attemptBackendLogin({
                user,
                email: user.email,
                photoUrl: user.photoURL,
                trackMixpanelEvent: false,
                showUserMessages: false,
            });

            if (!result.ok && result.shouldRetry) {
                scheduleBackendLoginRetry();
            } else if (result.ok) {
                clearBackendLoginRetry();
            }
        }, delayMs);
    }, [firebaseAuth, attemptBackendLogin, clearBackendLoginRetry]);

    useEffect(() => {
        return () => clearBackendLoginRetry();
    }, [clearBackendLoginRetry]);

    // ==================== Core Authentication Logic ====================

    /**
     * IMPORTANT: Firebase Auth Principles
     * 
     * 1. onAuthStateChanged fires when:
     *    - User signs in
     *    - User signs out
     *    - Page loads and Firebase restores the session
     * 
     * 2. onIdTokenChanged fires when:
     *    - All of the above, PLUS
     *    - Token is refreshed (every ~55 minutes automatically)
     * 
     * 3. Firestore automatically handles token refresh internally.
     *    You don't need to manually refresh tokens for Firestore operations.
     * 
     * 4. NEVER sign out the user due to:
     *    - Network errors
     *    - Backend API errors (500, etc.)
     *    - Token refresh failures (Firebase will retry)
     * 
     * 5. Only sign out when:
     *    - User explicitly logs out
     *    - Firebase reports user is null (session truly ended)
     *    - User account is disabled (auth/user-disabled)
     */

    // Primary auth state listener - handles sign-in/sign-out events
    useEffect(() => {
        if (isDesktopLocalMode) return;
        const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
            try {
                if (!user) {
                    // Check if this is a spurious sign-out (not user-initiated)
                    if (lastKnownUserRef.current && !logoutIntentRef.current) {
                        console.warn("Firebase Auth: Unexpected user=null, checking for recovery...");

                        // Give Firebase Auth a moment to recover, then check currentUser
                        await new Promise(resolve => setTimeout(resolve, 100));

                        const currentUser = firebaseAuth.currentUser;
                        if (currentUser) {
                            console.log("Firebase Auth: Recovered - user still authenticated:", currentUser.uid);
                            try {
                                const idToken = await currentUser.getIdToken();
                                authStorage.setItem(kLibrettoToken, idToken);
                                setAuthState(prevState => ({...prevState, token: idToken}));
                            } catch (e) {
                                console.warn("Token refresh during recovery failed:", e);
                            }
                            return;
                        }

                        // Double-check: if we still have valid stored auth info and no logout intent,
                        // try one more recovery attempt.
                        const storedToken = authStorage.getItem(kLibrettoToken);
                        const storedUserId = authStorage.getItem(kLibrettoUserId);
                        if (storedToken && storedUserId) {
                            console.warn("Firebase Auth: Stored session looks valid, delaying sign-out...");
                            await new Promise(resolve => setTimeout(resolve, 500));
                            const recoveredUser = firebaseAuth.currentUser;
                            if (recoveredUser) {
                                console.log("Firebase Auth: Late recovery successful:", recoveredUser.uid);
                                return;
                            }
                        }

                        console.log("Firebase Auth: Recovery failed, proceeding with sign-out");
                    }

	                    console.log("Firebase Auth: User signed out");
	                    lastKnownUserRef.current = null;
	                    clearBackendLoginRetry();
	                    clearAuthInfo();
	                    return;
	                }

                console.log("Firebase Auth: User signed in/restored", user.uid);
                lastKnownUserRef.current = user.uid;

	                const storedUserId = authStorage.getItem(kLibrettoUserId);
	                const isSameUserAsStored = !storedUserId || storedUserId === user.uid;
	                if (!isSameUserAsStored) {
	                    // Stored auth belongs to a different user; reset all persisted auth state.
	                    clearBackendLoginRetry();
	                    clearAuthInfo();
	                }
                // Keep ref in sync with persistent storage (e.g., when another tab completes backend auth).
                backendAuthenticatedRef.current = authStorage.getItem(kBackendAuthenticated) === 'true';

                // Avoid duplicate initialization
                if (authInitInProgressRef.current) {
                    return;
                }

                // If we already have backend auth for this user, just ensure token is fresh
                if (backendAuthenticatedRef.current && isSameUserAsStored) {
                    try {
                        const idToken = await user.getIdToken();
                        authStorage.setItem(kLibrettoToken, idToken);
                        // Ensure core user info exists even if storage was empty/stale.
                        if (authStorage.getItem(kIsAuthenticated) !== 'true') {
                            authStorage.setItem(kIsAuthenticated, 'true');
                        }
                        if (authStorage.getItem(kUserEmailVerified) !== 'true') {
                            authStorage.setItem(kUserEmailVerified, 'true');
                        }
                        authStorage.setItem(kLibrettoUserId, user.uid);
                        authStorage.setItem(kLibrettoEmail, user.email ?? "");
                        if (!authStorage.getItem(kLibrettoDisplayName) && user.displayName) {
                            authStorage.setItem(kLibrettoDisplayName, user.displayName);
                        }
                        if (!authStorage.getItem(kLibrettoPhotoUrl) && user.photoURL) {
                            authStorage.setItem(kLibrettoPhotoUrl, user.photoURL);
                        }
                        setAuthState(prevState => ({
                            ...prevState,
                            token: idToken,
                            userId: user.uid,
                            email: user.email,
                            displayName: prevState.displayName ?? user.displayName,
                            photoUrl: prevState.photoUrl ?? user.photoURL,
                        }));
                    } catch (error) {
                        console.error("Error refreshing token on auth restore:", error);
                    }
                    return;
                }

                // Don't auto-call backend /login if an explicit sign-in/sign-up flow is already doing it.
                if (suppressAutoBackendLoginRef.current) {
                    try {
                        const idToken = await user.getIdToken();
                        authStorage.setItem(kLibrettoToken, idToken);
                        setAuthState(prevState => ({
                            ...prevState,
                            token: idToken,
                            userId: user.uid,
                            email: user.email,
                            displayName: user.displayName,
                            photoUrl: user.photoURL,
                        }));
                    } catch (_e) {
                        // Best-effort; explicit flow will complete the login.
                    }
                    return;
                }

	                // First-time (or cross-tab) authentication - validate with backend
	                authInitInProgressRef.current = true;
	                try {
	                    const result = await attemptBackendLogin({
	                        user,
	                        email: user.email,
	                        photoUrl: user.photoURL,
	                        trackMixpanelEvent: false,
	                        showUserMessages: false,
	                    });

	                    if (!result.ok) {
	                        // Keep Firebase session alive and allow retry.
	                        if (!backendAuthenticatedRef.current) {
	                            authStorage.setItem(kBackendAuthenticated, 'false');
	                        }

	                        // Store basic Firebase auth info (but keep app auth gated).
	                        const idToken = await user.getIdToken().catch(() => null);
	                        if (idToken) {
	                            authStorage.setItem(kLibrettoToken, idToken);
	                        }
	                        setAuthState(prevState => ({
	                            ...prevState,
	                            token: idToken ?? prevState.token,
	                            userId: user.uid,
	                            email: user.email,
	                            displayName: user.displayName,
	                            photoUrl: user.photoURL,
	                        }));

	                        if (result.shouldRetry) {
	                            scheduleBackendLoginRetry();
	                        }
	                    }
	                } finally {
	                    authInitInProgressRef.current = false;
	                }
            } finally {
                markAuthReady();
            }
        });

        return () => unsubscribe();
	    }, [firebaseAuth, clearAuthInfo, markAuthReady, attemptBackendLogin, scheduleBackendLoginRetry, clearBackendLoginRetry, isDesktopLocalMode]);

    // Token refresh listener - keeps stored token fresh for API calls
    // Note: This does NOT affect Firestore - Firestore handles its own token refresh
	    useEffect(() => {
            if (isDesktopLocalMode) return;
	        const unsubscribe = onIdTokenChanged(firebaseAuth, async (user) => {
	            if (!user) return;

	            try {
	                const idToken = await user.getIdToken();
	                const currentToken = authStorage.getItem(kLibrettoToken);
	                
	                // Only update if token actually changed
	                if (idToken !== currentToken) {
	                    console.log("Firebase Auth: Token refreshed");
	                    authStorage.setItem(kLibrettoToken, idToken);
	                    setAuthState(prevState => ({...prevState, token: idToken}));
	                }
	            } catch (error) {
	                // Token refresh failed - Firebase will retry automatically
	                // Don't sign out or take any drastic action
                console.warn("Token refresh failed, Firebase will retry:", error.message);
            }
        });

        return () => unsubscribe();
	    }, [firebaseAuth, isDesktopLocalMode]);

    // Keep subscription status/plan in sync with Firestore so route gating unblocks
    // immediately after Stripe webhook-driven status changes (without page refresh).
    useEffect(() => {
        if (isDesktopLocalMode) return;
        const userId = authState.userId || authStorage.getItem(kLibrettoUserId);
        const backendAuthenticated = authStorage.getItem(kBackendAuthenticated) === 'true';
        if (!userId || !backendAuthenticated) return;

        const userRef = doc(db, kLibrettoUserCollectionName, userId);
        const unsubscribe = onSnapshot(
            userRef,
            (docSnap) => {
                if (!docSnap.exists()) return;
                const data = docSnap.data() || {};

                const nextStatus = typeof data.subscriptionStatus === 'string'
                    ? data.subscriptionStatus
                    : authStorage.getItem(kSubscriptionStatus) || "";
                const nextPlan = data.plan;
                const nextRoomName = typeof data.roomName === 'string'
                    ? data.roomName
                    : authStorage.getItem(kLibrettoUserRoomName) || "";
                const nextHasExportedAComposition = data.hasExportedAComposition === true;

                const nextPlanString = nextPlan == null
                    ? authStorage.getItem(kLibrettoUserPlan) || ""
                    : String(nextPlan);
                const previousStatus = authStorage.getItem(kSubscriptionStatus) || "";
                const previousPlan = authStorage.getItem(kLibrettoUserPlan) || "";
                const previousRoomName = authStorage.getItem(kLibrettoUserRoomName) || "";
                const previousHasExportedAComposition = authStorage.getItem(kHasExportedAComposition) === 'true';

                if (previousStatus !== nextStatus) {
                    authStorage.setItem(kSubscriptionStatus, nextStatus);
                }
                if (previousPlan !== nextPlanString) {
                    authStorage.setItem(kLibrettoUserPlan, nextPlanString);
                }
                if (previousRoomName !== nextRoomName) {
                    authStorage.setItem(kLibrettoUserRoomName, nextRoomName);
                }
                if (previousHasExportedAComposition !== nextHasExportedAComposition) {
                    authStorage.setItem(kHasExportedAComposition, nextHasExportedAComposition ? 'true' : 'false');
                }

                setAuthState((prevState) => {
                    const resolvedPlan = nextPlan == null ? prevState.userPlan : nextPlan;
                    if (
                        prevState.subscriptionStatus === nextStatus &&
                        prevState.userPlan === resolvedPlan &&
                        prevState.roomName === nextRoomName &&
                        prevState.hasExportedAComposition === nextHasExportedAComposition
                    ) {
                        return prevState;
                    }
                    return {
                        ...prevState,
                        subscriptionStatus: nextStatus,
                        userPlan: resolvedPlan,
                        roomName: nextRoomName,
                        hasExportedAComposition: nextHasExportedAComposition,
                    };
                });
            },
            (error) => {
                console.error("Error syncing subscription status from Firestore:", error);
            }
        );

        return () => unsubscribe();
    }, [authState.userId, authState.isAuthenticated, isDesktopLocalMode]);

    // Proactive token refresh - ensures token is fresh before it expires
    // This is a safety net, not the primary refresh mechanism
    const lastTokenRefreshRef = useRef(Date.now());
    
    useEffect(() => {
            if (isDesktopLocalMode) return;
	        const refreshTokenProactively = async (forceRefresh = false) => {
	            const user = firebaseAuth.currentUser;
	            if (!user) return;

	            try {
	                const idToken = await user.getIdToken(forceRefresh);
	                const currentToken = authStorage.getItem(kLibrettoToken);
	                
	                // Only update state if token actually changed to avoid unnecessary re-renders
	                if (idToken !== currentToken) {
	                    authStorage.setItem(kLibrettoToken, idToken);
	                    setAuthState(prevState => ({...prevState, token: idToken}));
	                    lastTokenRefreshRef.current = Date.now();
	                }
	            } catch (error) {
	                // Don't sign out on refresh errors
                console.warn("Proactive token refresh failed:", error.message);
            }
        };

        // Refresh every 45 minutes (tokens expire at 60 minutes)
        const intervalId = setInterval(() => refreshTokenProactively(true), 45 * 60 * 1000);

        // On visibility change, only refresh if it's been more than 10 minutes
        // and use non-forced refresh (let Firebase decide if needed)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const timeSinceLastRefresh = Date.now() - lastTokenRefreshRef.current;
                const tenMinutesMs = 10 * 60 * 1000;
                
                if (timeSinceLastRefresh > tenMinutesMs) {
                    refreshTokenProactively(false); // Non-forced - Firebase decides if refresh needed
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [firebaseAuth, isDesktopLocalMode]);

    // ==================== Public Methods ====================

    const logout = useCallback(async () => {
        if (isDesktopLocalMode) {
            return;
        }
        // Mark that logout was intentional to prevent recovery logic from blocking it
        logoutIntentRef.current = true;
        lastKnownUserRef.current = null;
        clearBackendLoginRetry();
        clearAuthInfo();
        await signOut(firebaseAuth);
        // Reset the intent after sign-out completes
        logoutIntentRef.current = false;
    }, [firebaseAuth, clearAuthInfo, clearBackendLoginRetry, isDesktopLocalMode]);

    const isUserAuthenticated = useCallback(() => {
        if (isDesktopLocalMode) return true;
        if (!authReadyRef.current) return false;
        return authStorage.getItem(kIsAuthenticated) === 'true' && 
               authStorage.getItem(kUserEmailVerified) === 'true' && 
               authStorage.getItem(kBackendAuthenticated) === 'true';
    }, [isDesktopLocalMode]);

    const isUserSubscriptionActive = useCallback(() => {
        return true;
    }, []);

    const getUserDisplayName = () => authStorage.getItem(kLibrettoDisplayName);
    const getUserRoomName = () => authStorage.getItem(kLibrettoUserRoomName);
    const getUserPlan = () => authStorage.getItem(kLibrettoUserPlan);
    const getUserEmail = () => authStorage.getItem(kLibrettoEmail);
    const getUserPhotoUrl = () => authStorage.getItem(kLibrettoPhotoUrl);
    const getUserId = () => authStorage.getItem(kLibrettoUserId);
    const getDefaultWorkspaceId = () => authStorage.getItem(kLibrettoDefaultWorkspaceId) || authStorage.getItem(kLibrettoUserId);
    const getWorkspaces = () => normalizeWorkspaceList(safeJsonParse(authStorage.getItem(kLibrettoWorkspaces), []));
    const getCurrentWorkspaceId = () => authStorage.getItem(kLibrettoCurrentWorkspaceId) || getDefaultWorkspaceId();
    const getCurrentWorkspaceRole = () => {
        const wid = getCurrentWorkspaceId();
        const ws = getWorkspaces().find(w => w.workspaceId === wid);
        return ws?.role || 'viewer';
    };
    const isCurrentWorkspaceEditor = () => {
        const role = getCurrentWorkspaceRole();
        return role === 'owner' || role === 'editor';
    };
    const setCurrentWorkspaceId = (workspaceId) => {
        const wid = String(workspaceId || '').trim();
        if (!wid) return;
        authStorage.setItem(kLibrettoCurrentWorkspaceId, wid);
        setAuthState(prevState => ({...prevState, currentWorkspaceId: wid}));
    };

    const upsertWorkspaceInList = (workspace) => {
        const workspaceId = String(workspace?.workspaceId ?? '').trim();
        if (!workspaceId) return;

        const entry = {
            workspaceId,
            name: typeof workspace?.name === 'string' ? workspace.name : '',
            role: typeof workspace?.role === 'string' ? workspace.role : '',
            ownerUserId: typeof workspace?.ownerUserId === 'string' ? workspace.ownerUserId : '',
        };

        const existing = getWorkspaces();
        const next = normalizeWorkspaceList([
            ...existing.filter((w) => w.workspaceId !== workspaceId),
            entry,
        ]);

        authStorage.setItem(kLibrettoWorkspaces, JSON.stringify(next));
        setAuthState(prevState => ({...prevState, workspaces: next}));
    };
    const getLibrettoLiveblocksToken = () => tabStorage.getItem(kLibrettoLiveblocksToken);
    const getLibrettoCurrentProjectId = () => tabStorage.getItem(kLibrettoCurrentProjectId);
    const hasUpgradeIntent = () => authStorage.getItem(kUpgradeIntent) === 'true';
    const clearUpgradeIntent = () => authStorage.removeItem(kUpgradeIntent);
    const isTestUser = () => authStorage.getItem(kIsTestUser) === 'true';
    const getHasLegacyProjects = () => authStorage.getItem(kHasLegacyProjects) === 'true';
    const hasExportedAComposition = () => authStorage.getItem(kHasExportedAComposition) === 'true';

    const getUser = useCallback(() => ({
        id: authStorage.getItem(kLibrettoUserId),
        name: authStorage.getItem(kLibrettoDisplayName),
        email: authStorage.getItem(kLibrettoEmail),
        createdAt: authStorage.getItem(kLibrettoUserCreatedAt),
        plan: authStorage.getItem(kLibrettoUserPlan),
    }), []);

    // ==================== Sign In / Sign Up Methods ====================

    const login = async ({token, email, photoUrl, user}) => {
        const result = await attemptBackendLogin({
            user,
            token,
            email,
            photoUrl,
            trackMixpanelEvent: true,
            showUserMessages: true,
        });
        if (!result.ok && result.shouldRetry) {
            scheduleBackendLoginRetry();
        }
        return result;
    };

    const signup = async ({token, displayName, photoUrl, email, user, codes}) => {
        try {
            const effectivePlan = LibrettoPlan.StudioFree;
            const signupPayload = {
                displayName,
                plan: effectivePlan,
                code: "",
                codeTwo: "",
                email,
            };
            if (Array.isArray(codes) && codes.length > 0) signupPayload.codes = codes;

            const attribution = getStoredSignupAttribution() || maybeCaptureSignupAttributionFirstTouch();
            if (attribution) {
                signupPayload.attribution = attribution;
            }

            const response = await fetchContext.authAxios.post('/signup', signupPayload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.status === 200) {
                authStorage.removeItem(kSignupAttributionFirstTouch);
                mixpanel.track('Sign Up', {
                    'email': email,
                    'userId': user.uid,
                    'plan': effectivePlan
                });
                handleSignupFormClose();
                return await login({token, email, photoUrl, user});
            } else if (response.status === 202) {
                authStorage.removeItem(kSignupAttributionFirstTouch);
                setSignUpStateMessage({forSignup: true, forSignIn: false, message: "An email has been sent to you. Please verify your email address before logging in!", user: null});
                setSignUpStateMessageOpen(true);
                return {ok: true, emailVerificationRequired: true};
            }
        } catch (error) {
            if (error.response?.status === 500) {
                const isWelcomePlan = selectedLibrettoPlanForSignup === LibrettoPlan.StudioLifetime;
                const message = isWelcomePlan 
                    ? "Error. Make sure you have entered a valid code and try again!" 
                    : "Internal server error. Please try again later!";
                setSignUpStateMessage({forSignup: true, forSignIn: false, message, user: null});
                setSignUpStateMessageOpen(true);
            }
            return {ok: false, error};
        }
        return {ok: false};
    };

    const signinWithGoogle = async () => {
        suppressAutoBackendLoginRef.current = true;
        try {
            const result = await signInWithPopup(firebaseAuth, provider);
            const idToken = await result.user.getIdToken(true);
            await login({token: idToken, email: result.user.email, photoUrl: result.user.photoURL, user: result.user});
            handleLoginFormClose();
        } catch (error) {
            if (error.code === "auth/user-disabled") {
                setSignUpStateMessage({forSignup: false, forSignIn: true, message: "Unable to login. Please email contact@libretto.fm if this persists.", user: null});
                setSignUpStateMessageOpen(true);
            } else if (error.code === "auth/popup-blocked") {
                showAuthPopupBlockedMessage("signin");
            } else if (error.code !== "auth/popup-closed-by-user") {
                console.error("Google sign-in error:", error);
            }
        } finally {
            suppressAutoBackendLoginRef.current = false;
        }
    };

    const signinWithEmail = async ({email, password}) => {
        suppressAutoBackendLoginRef.current = true;
        try {
            await setPersistence(firebaseAuth, browserLocalPersistence);
            await signInWithEmailAndPassword(firebaseAuth, email, password);
            const idToken = await firebaseAuth.currentUser.getIdToken(true);
            const result = await login({token: idToken, email, photoUrl: "", user: firebaseAuth.currentUser});
            handleLoginFormClose();
            return result;
        } catch (error) {
            if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
                setSignUpStateMessage({forSignup: false, forSignIn: true, message: "Invalid email/password. Make sure you have an account created and enter valid credentials!", user: null});
                setSignUpStateMessageOpen(true);
            } else if (error.code === "auth/user-disabled") {
                setSignUpStateMessage({forSignup: false, forSignIn: true, message: "Unable to login. Please email contact@libretto.fm if this persists.", user: null});
                setSignUpStateMessageOpen(true);
            }
            return {ok: false, error};
        } finally {
            suppressAutoBackendLoginRef.current = false;
        }
    };

    const signupWithGoogle = async (options = {}) => {
        suppressAutoBackendLoginRef.current = true;
        try {
            const userCredential = await signInWithPopup(firebaseAuth, provider);
            const idToken = await userCredential.user.getIdToken(true);
            const result = await signup({
                token: idToken,
                displayName: userCredential.user.displayName,
                photoUrl: userCredential.user.photoURL,
                email: userCredential.user.email,
                user: userCredential.user,
                planOverride: options.planOverride,
                codes: options.codes,
            });
            handleSignupFormClose();
            return result;
        } catch (error) {
            if (error.code === "auth/popup-blocked") {
                showAuthPopupBlockedMessage("signup");
            } else if (error.code !== "auth/popup-closed-by-user") {
                console.error("Google sign-up error:", error);
            }
            return {ok: false, error};
        } finally {
            suppressAutoBackendLoginRef.current = false;
        }
    };

    const signUpWithEmailAndPassword = async ({displayName, email, password, planOverride, codes, newAccountOnly = false}) => {
        suppressAutoBackendLoginRef.current = true;
        try {
            await setPersistence(firebaseAuth, browserLocalPersistence);
            if (newAccountOnly) {
                let user = firebaseAuth.currentUser;
                if (!user || user.email?.toLowerCase() !== email.toLowerCase()) {
                    const created = await createUserWithEmailAndPassword(firebaseAuth, email, password);
                    user = created.user;
                    await sendEmailVerification(user);
                }
                const token = await user.getIdToken(true);
                return await signup({displayName, token, photoUrl: "", email, user, planOverride, codes});
            }
            // Try to sign in first (in case user already exists)
            await signInWithEmailAndPassword(firebaseAuth, email, password);
            const idToken = await firebaseAuth.currentUser.getIdToken(true);
            const result = await login({token: idToken, email, photoUrl: "", user: firebaseAuth.currentUser});
            handleLoginFormClose();
            return result;
        } catch (error) {
            if (error.code === "auth/wrong-password") {
                // User exists but wrong password - switch to login form
                setSignupFormOpen(false);
                setLoginFormOpen(true);
            } else if (error.code === "auth/user-disabled") {
                setLoginFormOpen(false);
                setSignUpStateMessage({forSignup: false, forSignIn: true, message: "Unable to login. Please email contact@libretto.fm if this persists.", user: null});
                setSignUpStateMessageOpen(true);
            } else if (error.code === "auth/user-not-found" || error.code === "auth/invalid-credential") {
                // Create new user
                try {
                    const newUserCredential = await createUserWithEmailAndPassword(firebaseAuth, email, password);
                    await sendEmailVerification(newUserCredential.user);
                    const idToken = await newUserCredential.user.getIdToken(true);
                    const result = await signup({displayName, token: idToken, photoUrl: "", email, user: newUserCredential.user, planOverride});
                    handleSignupFormClose();
                    return result;
                } catch (signUpError) {
                    console.error("Error creating user:", signUpError);
                    return {ok: false, error: signUpError};
                }
            }
            return {ok: false, error};
        } finally {
            suppressAutoBackendLoginRef.current = false;
        }
    };

    const sendPasswordResetEmailForUser = ({email, setResetEmailSent, setResetEmailError}) => {
        sendPasswordResetEmail(firebaseAuth, email)
            .then(() => {
                console.log("Password reset email sent!");
                setResetEmailSent(true);
            })
            .catch((error) => {
                console.error("Error sending password reset email:", error);
                setResetEmailError(true);
            });
    };

    // Form handlers
    const handleLoginFormOpen = () => setLoginFormOpen(true);
    const handleLoginFormClose = () => setLoginFormOpen(false);
    const handleSignupFormOpen = (options = {}) => {
        setSelectedLibrettoPlanForSignup(normalizeSignupPlanSelection(options));
        const upgradeIntent = options && typeof options === 'object' ? options.upgradeIntent : undefined;
        if (upgradeIntent) {
            authStorage.setItem(kUpgradeIntent, String(upgradeIntent));
        }
        setSignupFormOpen(true);
    };
    const handleSignupFormClose = () => setSignupFormOpen(false);
    const handleSignupStateMessageClose = () => setSignUpStateMessageOpen(false);

    return (
        <Provider
            value={{
                authReady,
                authState,
                logout,
                isUserAuthenticated,
                isUserSubscriptionActive,
                getToken,
                getFreshToken, // New: Always returns a valid token for API calls
                refreshToken,
                getLibrettoLiveblocksToken,
                getSubscriptionStatusFromStorage,
                getLibrettoCurrentProjectId,
                getUserId,
                getUserDisplayName,
                getUserRoomName,
                getUserPlan,
                hasUpgradeIntent,
                clearUpgradeIntent,
                getUserEmail,
                getUser,
                getUserPhotoUrl,
                signinWithGoogle,
                signupWithGoogle,
                handleLoginFormOpen,
                handleLoginFormClose,
                handleSignupFormOpen,
                handleSignupFormClose,
                loginFormOpen,
                signupFormOpen,
                signUpStateMessage,
                signUpStateMessageOpen,
                sendPasswordResetEmailForUser,
                handleSignupStateMessageClose,
                signUpWithEmailAndPassword,
                signinWithEmail,
                isTestUser,
                getHasLegacyProjects,
                hasExportedAComposition,
                getDefaultWorkspaceId,
                getWorkspaces,
                getCurrentWorkspaceId,
                getCurrentWorkspaceRole,
                isCurrentWorkspaceEditor,
                setCurrentWorkspaceId,
                upsertWorkspaceInList,
            }}
        >
            {children}
        </Provider>
    );
};

export { AuthProvider, AuthContext };
